import Input from 'components/final-form/Input';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Loader, Modal } from 'semantic-ui-react';
import { editEmployeeEmail } from 'services/employeeServices';
import axios from 'services/http';
import { getPatientForPatientCentral } from 'services/patientServices';
import { InterventionDto, PatientInfoDto } from 'ts-types/api.types';
import { errorUtils } from 'utils/errorUtils';
import { emailValidator } from 'utils/validatorUtils';

interface Props extends MessageSourceProps {
  open: boolean;
  patientId: number;
  onClose: () => void;
}

interface State {
  patient?: PatientInfoDto;
  errorMessage?: string;
  requiredDataLoaded: boolean;
}

class ChangeEmployeeEmailModal extends Component<Props, State> {

  cancelTokenSource = axios.CancelToken.source();

  private initialValues: Partial<PatientInfoDto> = {};

  interventionMap: { [id: number]: InterventionDto } = {};

  constructor(props: Props) {
    super(props);

    this.state = {
      requiredDataLoaded: false
    }
  }

  componentDidMount() {
    this.fetchEmployee();
  }

  fetchEmployee = async () => {
    const {patientId} = this.props;
    try {
      const patient = await getPatientForPatientCentral(patientId, this.cancelTokenSource);

      this.initialValues = {email: patient ? patient.email : ""};

      this.setState({
        patient
      });
    } finally {
      this.setState({
        requiredDataLoaded: true
      });
    }
  }

  submitEmployeeEmail = async (values: Partial<PatientInfoDto>) => {
    const {patient} = this.state;

    if (!patient || !values.email) {
      return;
    }

    await editEmployeeEmail(patient.employeeId, values.email, this.cancelTokenSource)
        .then(this.props.onClose)
        .catch((e: any) => this.handleError(e.response.data))
  }

  handleError(error: any) {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors: Array<string> = [
      errorUtils.invalidEmail,
      errorUtils.employeeNotFound
    ];

    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          this.setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        this.setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        this.setErrorMessage(getMessage('error.general'));
      }
    }
  }

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  render(): React.ReactNode {

    const {open, onClose, getMessage} = this.props;
    const {requiredDataLoaded} = this.state;

    return (
        <Modal
            open={open}
            onClose={onClose}
            closeOnDimmerClick={false}
            style={{width: "30rem"}}
        >
          <Modal.Header>{getMessage("employee.changeEmail.title")}</Modal.Header>
          <FormModalContentContainer>
            {
              requiredDataLoaded
                  ? <FinalForm
                      onSubmit={(values) => this.submitEmployeeEmail(values)}
                      subscription={{pristine: true, submitting: true, values: true}}
                      initialValues={this.initialValues}
                      render={this.renderForm}
                  />
                  : <Loader active size="small" inline="centered" inverted/>
            }
          </FormModalContentContainer>
        </Modal>
    );
  }

  renderForm = ({handleSubmit, submitting}: FormRenderProps): React.ReactNode => {

    const {onClose, getMessage} = this.props;
    const {errorMessage} = this.state;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            {
                errorMessage
                && <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                  {errorMessage}
                </StyledErrorMessage>
            }
            <PraxFormGrid>
              <Grid.Row>
                <Grid.Column width={2} verticalAlign="middle">
                  <label className="patient-info-property">
                    {getMessage("patient.email")}:
                  </label>
                </Grid.Column>
                <Grid.Column width={14} verticalAlign="middle">
                  <Field
                      name="email"
                      component={Input}
                      fluid
                      validate={emailValidator}
                  />
                </Grid.Column>
              </Grid.Row>
            </PraxFormGrid>
            <FormButtonsRow>
              <Button compact type="submit" primary disabled={submitting}>
                {getMessage("button.save")}
              </Button>
              <Button compact type="button" disabled={submitting}
                      onClick={() => onClose()}>{getMessage("button.cancel")}
              </Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  }

}

export default withMessages(ChangeEmployeeEmailModal);
